import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { InvoicePdfAttachmentEnum, InvoicePdfEnum } from '@wilson/interfaces';
import {
  ControlValueAccessorDirective,
  FormFieldComponent,
} from '@wilson/forms';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

import {
  BillingFormAttachmentActivityInfoType,
  BillingFormAttachmentGroupingType,
  BillingFormAttachmentShiftInfoType,
  BillingFormInvoicePdfType,
  transformAttachmentTypeToUserInput,
  transformPdfTypesToUserInput,
  transformUserInputToAttachmentType,
  transformUserInputToPdfType,
} from './invoice-pdf-settings-form.utils';

const ITERABLE_PDF_TYPES = Object.entries(BillingFormInvoicePdfType);
const ITERABLE_GROUPING_TYPES = Object.entries(
  BillingFormAttachmentGroupingType,
);
const ITERABLE_SHIFT_INFO_TYPES = Object.entries(
  BillingFormAttachmentShiftInfoType,
);
const ITERABLE_ACTIVITY_INFO_TYPES = Object.entries(
  BillingFormAttachmentActivityInfoType,
);

export type InvoicePdfSettingsFormValue = {
  invoicePdfType: InvoicePdfEnum;
  invoicePdfAttachmentType: InvoicePdfAttachmentEnum;
};

@Component({
  selector: 'wilson-invoice-pdf-settings-form',
  templateUrl: './invoice-pdf-settings-form.component.html',
  styleUrls: ['./invoice-pdf-settings-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  hostDirectives: [ControlValueAccessorDirective],
  imports: [
    ReactiveFormsModule,
    NzSelectModule,
    NzTooltipModule,
    TranslatePipe,
    FormFieldComponent,
  ],
})
export class InvoicePdfSettingsFormComponent {
  readonly editable = input<boolean>(true);
  readonly showAttachmentLegend = input<boolean>(false);
  readonly showStacked = input<boolean>(false);

  protected readonly cva = inject(
    ControlValueAccessorDirective<InvoicePdfSettingsFormValue>,
  );

  protected readonly form = new FormGroup({
    invoicePdfTypes: new FormControl<BillingFormInvoicePdfType[] | null>(
      [BillingFormInvoicePdfType.INVOICE, BillingFormInvoicePdfType.ATTACHMENT],
      Validators.required,
    ),
    attachmentGrouping:
      new FormControl<BillingFormAttachmentGroupingType | null>(
        null,
        Validators.required,
      ),
    attachmentShiftInfo:
      new FormControl<BillingFormAttachmentShiftInfoType | null>(null),
    attachmentActivityInfo:
      new FormControl<BillingFormAttachmentActivityInfoType | null>(null),
  });

  protected readonly BillingFormAttachmentGroupingType =
    BillingFormAttachmentGroupingType;
  protected readonly iterablePdfTypes = ITERABLE_PDF_TYPES;
  protected readonly iterableGroupingTypes = ITERABLE_GROUPING_TYPES;
  protected readonly iterableShiftInfoTypes = ITERABLE_SHIFT_INFO_TYPES;
  protected readonly iterableActivityInfoTypes = ITERABLE_ACTIVITY_INFO_TYPES;

  protected readonly invoicePdfTypesValue = signal(
    this.form.controls.invoicePdfTypes.value,
  );

  protected readonly groupingValue = signal(
    this.form.controls.attachmentGrouping.value,
  );

  protected readonly attachmentSelected = computed(
    () =>
      this.invoicePdfTypesValue()?.includes(
        BillingFormInvoicePdfType.ATTACHMENT,
      ) ?? false,
  );

  constructor() {
    this.form.controls.invoicePdfTypes.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((v) => this.invoicePdfTypesValue.set(v));

    this.form.controls.attachmentGrouping.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((v) => this.groupingValue.set(v));

    // CVA value → internal form (writeValue bridge)
    effect(() => {
      const val = this.cva.value();
      if (val) {
        this.form.patchValue(
          {
            invoicePdfTypes: transformPdfTypesToUserInput(val.invoicePdfType),
            ...transformAttachmentTypeToUserInput(val.invoicePdfAttachmentType),
          },
          { emitEvent: false },
        );
        this.invoicePdfTypesValue.set(this.form.controls.invoicePdfTypes.value);
        this.groupingValue.set(this.form.controls.attachmentGrouping.value);
      }
    });

    // CVA disabled + pdf types + grouping → enable/disable sub-controls
    effect(() => {
      const disabled = this.cva.disabled();
      const hasAttachment = !disabled && this.attachmentSelected();

      if (disabled) {
        this.form.disable({ emitEvent: false });
        return;
      }

      this.form.enable({ emitEvent: false });

      if (!hasAttachment) {
        this.form.controls.attachmentGrouping.disable({ emitEvent: false });
        this.#setSubControlState(false, false);
        return;
      }

      const grouping = this.groupingValue();
      const isActivity =
        grouping === BillingFormAttachmentGroupingType.GROUPED_BY_ACTIVITY;
      const isShift =
        grouping === BillingFormAttachmentGroupingType.GROUPED_BY_SHIFT;

      this.#setSubControlState(!isActivity && !isShift, isActivity);
    });

    // Internal form changes → CVA onChange
    this.form.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.cva.updateValue(this.#buildExternalValue()));
  }

  #setSubControlState(shiftEnabled: boolean, activityEnabled: boolean): void {
    const shift = this.form.controls.attachmentShiftInfo;
    const activity = this.form.controls.attachmentActivityInfo;

    if (shiftEnabled) {
      shift.enable({ emitEvent: false });
      shift.setValidators([Validators.required]);
    } else {
      shift.disable({ emitEvent: false });
      shift.clearValidators();
    }
    shift.updateValueAndValidity({ emitEvent: false });

    if (activityEnabled) {
      activity.enable({ emitEvent: false });
      activity.setValidators([Validators.required]);
    } else {
      activity.disable({ emitEvent: false });
      activity.clearValidators();
    }
    activity.updateValueAndValidity({ emitEvent: false });
  }

  #buildExternalValue(): InvoicePdfSettingsFormValue {
    const raw = this.form.getRawValue();
    return {
      invoicePdfType: transformUserInputToPdfType(raw.invoicePdfTypes ?? []),
      invoicePdfAttachmentType: raw.attachmentGrouping
        ? transformUserInputToAttachmentType(
            raw.attachmentGrouping,
            raw.attachmentShiftInfo,
            raw.attachmentActivityInfo,
          )
        : InvoicePdfAttachmentEnum.GROUPED_BY_USER_WITH_FULL_SHIFT_INFO,
    };
  }
}
